import { type TFunction } from 'i18next'

import { fiveSegmentLabelI18nKeyMap } from '~/utils'

import { type FiveSegmentsAnswerData } from './buildFiveSegmentsAnswersArrayData'
import { type NineSegmentsAnswerData } from './buildNineSegmentsAnswersArrayData'
import { type FiveSegmentColumnKey } from './fiveSegments'
import {
  type NineSegmentColumnKey,
  nineSegmentIndicatorLabelI18nKeyMap,
} from './nineSegments'

// AI コメント用に、ドライバー Top5 を SPA 側で確定させて表テキスト末尾に追記する。
//
// WHY: 9segs 顧客データ表の差分列は 14 本すべてヘッダーが「差分」(nineSegmentIndicatorLabelI18nKeyMap
// が difference / difference_1 … difference_13 を全部同じ訳にする) で、LLM は位置でしか列を特定
// できない。実測でオフバイワン (認知未購入の行がユーザー内の列を読む等) と Top5 の読み飛ばし
// (より大きい値を飛ばす) が繰り返し起きた。プロンプト側の指示 (位置アンカー・除外指示) では
// 直らないことも実測済みなので、選択そのものをコードで確定し、LLM の仕事を「書き写し」に落とす。
// appendSampleThresholdBlock と同じ append-only の凍結フォーマット契約。
//
// 見出しは日英併記の1本: analyzer の en プロンプトは日本語文字を持てない
// (languageVariants テストが図表本文の日本語を全面禁止) ため、en 側は ASCII 部分
// "Activation-driver Top5" / "Acquisition/loyalty-driver Top5" でこのブロックを参照する。
// 行頭の 1.〜5. の番号は出力フォーマットの行との位置対応 (1行目=積極全体…) を固定するためで、
// ラベル文字列は UI 言語に依存しても番号は変わらない。
export const NINE_SEG_DRIVER_BLOCK_HEADING =
  '# 積極化ドライバーTop5 / Activation-driver Top5（積極−消極の差分ptsを算出済み。この値だけを使い、表から選び直さない）'
export const FIVE_SEG_DRIVER_BLOCK_HEADING =
  '# 新規化・ロイヤル化ドライバーTop5 / Acquisition/loyalty-driver Top5（差分ptsを算出済み。この値だけを使い、表から選び直さない）'

// 見出し直下の注意書き。「ドライバーの完成品がある」こと自体に LLM が引っぱられて、
// イメージ設問でない設問 (重視すること等) まで場合分けB (ドライバー分析) で出力してしまう
// 事故がブロック導入時の検証で 5/5 再現した。主対策は下の isImageQuestionText による
// 付与ゲート (通常設問にはブロック自体を付けない) で、この注意書きはゲートをすり抜けた
// 場合の保険。プロンプト側の判定節にも同じガードがある。
export const DRIVER_BLOCK_CAVEAT =
  '※このブロックは表側がイメージ設問のとき（場合分けB）専用。イメージ設問以外の設問では、このブロックを無視し使わない / Use ONLY when the row is a brand-image question; otherwise ignore this block entirely'

// ブロックを付ける設問のゲート。イメージ設問でない設問にブロックを付けると、プロンプトで
// どれだけ禁止しても LLM がドライバー分析へ誤誘導される (注意書き+判定節+場合分けAの
// 3重ガードでも 5/5 で誤判定した実測)。設問種別のメタデータは無いので、プロンプトの判定
// ルールと同じく設問文の語彙で見分ける: 実データのイメージ設問は「…にあてはまるイメージを
// すべてお選びください」の形。含まないイメージ設問は従来動作 (ブロック無し → プロンプトの
// フォールバック手順) に落ちるだけで、現行より悪化しない。
// ウィザードの NPIドライバー役割のようにイメージ設問だと明示的に分かる呼び出し元は、
// このゲートを使わず常に付与してよい (呼び出し側で判断する)。
export const isImageQuestionText = (questionText: string): boolean =>
  /イメージ/.test(questionText) || /image/i.test(questionText)

// 正の差分が 1 件も無い組の行。「行ごと省略」にしないのは、行番号と出力行の位置対応を保つため。
//
// 差分列を非表示にした組もこの行に落とす (entries を空にする)。場合分けBは「ブロックの
// 1行目→出力の1行目 … 5行目→出力の5行目」という位置対応の契約で、出力フォーマット側も
// 5スロットに固定ラベルが付いている。行ごと落として詰め直すと、ブロック3行目=積極一般 に
// 対して出力3行目=積極離反 のラベルが付き、この実装が潰したはずの「項目と値のペアが崩れる」
// 事故そのものになる。プロンプトには「『正の差分なし』の行は、出力ではその行ごと省略する」
// (en: skip an output line when its block line is marked "none") という既存ルールがあるので、
// 同じマークに寄せることで analyzer 側を変更せずに行を消せる。
// セグメント別Top5ブロックが非表示列を「行ごと落として詰め直す」のと扱いが違うのは、
// 場合分けAが「セグメント数分 (ブロックの行数分) 繰り返す」= 行数駆動の契約だから。
export const DRIVER_NONE_MARK = '（正の差分なし / none）'

const TOP_N = 5

// ラベル内の改行 (積極\n認知未購入) と en ラベルのソフトハイフン (Active­Aware …) を潰して
// 1 行に収める。ソフトハイフンは幅ゼロの表示用文字でデータ照合には不要。
const toSingleLineLabel = (label: string): string =>
  label.replace(/­/g, '').replace(/\s*\n\s*/g, ' ')

// ratios の差分 (割合) を pts 表記へ。+18.5 のように符号を必ず付ける (正のみ載る)。
const formatPts = (ratio: number): string => `+${(ratio * 100).toFixed(1)}`

type DriverLine = {
  label: string
  // 項目名 → 差分 (ratio)。ここから正のみ降順 Top5 を確定する。
  entries: { text: string; diff: number }[]
}

const renderLine = (index: number, line: DriverLine): string => {
  const top = line.entries
    .filter((e) => Number.isFinite(e.diff) && 0 < e.diff)
    .sort((a, b) => b.diff - a.diff)
    .slice(0, TOP_N)
  const body =
    top.length === 0
      ? DRIVER_NONE_MARK
      : top.map((e) => `${e.text} ${formatPts(e.diff)}`).join(' / ')

  return `${index + 1}. ${toSingleLineLabel(line.label)}: ${body}`
}

const renderBlock = (
  heading: string,
  questions: { questionText: string; lines: DriverLine[] }[],
): string | null => {
  // entries が空の行があっても設問ごと落とさない。ブロックが丸ごと消えると、プロンプトの
  // 「ブロックが無い入力の場合のみ、…差分列から…5件選ぶ」フォールバックに転び、
  // 表から選び直す旧挙動 (= 列ズレ・読み飛ばし) に戻ってしまう。
  if (questions.length === 0) {
    return null
  }

  return [
    heading,
    DRIVER_BLOCK_CAVEAT,
    ...questions.flatMap((q) => [
      `## ${q.questionText}`,
      ...q.lines.map((line, i) => renderLine(i, line)),
    ]),
  ].join('\n')
}

// 構造的部分型: フックが返す実データ (NineSegmentsAnswerData) も、テストのモックも受けられる。
// ratios は使う差分キーだけに絞る (フル型を要求するとテストで全指標の構築が必要になる)。
type NineSegDriverSourceRow = {
  answerOption: { text: string }
  question: { question_text: string }
  ratios: Pick<
    NineSegmentsAnswerData['ratios'],
    | 'estrangedDiff'
    | 'generalDiff'
    | 'loyalDiff'
    | 'recognizedDiff'
    | 'totalDiff'
  >
}

/**
 * 9segs の積極化ドライバー Top5 ブロックを表テキスト末尾に追記します。
 *
 * 行は出力フォーマットと同じ順の 5 行固定:
 * 1. 積極全体 / 2. 積極 認知未購入 / 3. 積極離反 / 4. 積極一般 / 5. 積極ロイヤル。
 * 各行は正の差分のみを降順で最大 5 件。表テキストは一切変更しない (append-only)。
 *
 * 差分列を非表示にした組は「正の差分なし」行に落とす (行番号は 5 行のまま維持)。
 *
 * @param serialized - シリアライズ済みの表テキスト (しきいブロック等が付いていてもよい)。
 * @param answersArrayData - 表示中の回答行 (filterVisibleAnswersArrayData 適用済み)。
 * @param t - 行ラベル用の翻訳関数 (表と同じ UI 言語)。
 * @param hiddenColumnKeys - 非表示にされた列。ウィザードは常に非表示なしなので既定は空。
 * @returns ブロックを追記したテキスト。全設問が空なら入力をそのまま返す。
 */
export const appendNineSegDriverTop5Block = (
  serialized: string,
  answersArrayData: readonly (readonly NineSegDriverSourceRow[])[],
  t: TFunction,
  hiddenColumnKeys: readonly NineSegmentColumnKey[] = [],
): string => {
  // 出力フォーマットの行順と同じ。ラベルは表の列 (積極◯◯) と同じ i18n キーを使う。
  const pairs = [
    {
      labelKey: nineSegmentIndicatorLabelI18nKeyMap.totalPositive,
      diffKey: 'totalDiff',
    },
    {
      labelKey: nineSegmentIndicatorLabelI18nKeyMap.recognizedPositive,
      diffKey: 'recognizedDiff',
    },
    {
      labelKey: nineSegmentIndicatorLabelI18nKeyMap.estrangedPositive,
      diffKey: 'estrangedDiff',
    },
    {
      labelKey: nineSegmentIndicatorLabelI18nKeyMap.generalPositive,
      diffKey: 'generalDiff',
    },
    {
      labelKey: nineSegmentIndicatorLabelI18nKeyMap.loyalPositive,
      diffKey: 'loyalDiff',
    },
  ] as const

  const questions = answersArrayData
    .filter((rows) => 0 < rows.length)
    .map((rows) => ({
      questionText: rows[0].question.question_text,
      lines: pairs.map(({ diffKey, labelKey }) => ({
        label: t(labelKey),
        // 差分列が非表示なら値を持たせず DRIVER_NONE_MARK 行にする (上のコメント参照)。
        entries: hiddenColumnKeys.includes(diffKey)
          ? []
          : rows.map((row) => ({
              text: row.answerOption.text,
              diff: row.ratios[diffKey],
            })),
      })),
    }))

  const block = renderBlock(NINE_SEG_DRIVER_BLOCK_HEADING, questions)

  return block === null ? serialized : `${serialized}\n\n${block}`
}

type FiveSegDriverSourceRow = {
  answerOption: { text: string }
  question: { question_text: string }
  ratios: Pick<
    FiveSegmentsAnswerData['ratios'],
    'generalVsEstranged' | 'generalVsRecognized' | 'loyalVsGeneral'
  >
}

/**
 * 5segs の新規化・ロイヤル化ドライバー Top5 ブロックを表テキスト末尾に追記します。
 *
 * 行は出力フォーマットと同じ順の 3 行固定:
 * 1. 認知未購入→一般 / 2. 離反→一般 / 3. 一般→ロイヤル。
 *
 * 差分列を非表示にした組は「正の差分なし」行に落とす (行番号は 3 行のまま維持)。
 *
 * @param serialized - シリアライズ済みの表テキスト。
 * @param answersArrayData - 表示中の回答行 (filterVisibleAnswersArrayData 適用済み)。
 * @param t - 行ラベル用の翻訳関数 (表と同じ UI 言語)。
 * @param hiddenColumnKeys - 非表示にされた列。ウィザードは常に非表示なしなので既定は空。
 * @returns ブロックを追記したテキスト。全設問が空なら入力をそのまま返す。
 */
export const appendFiveSegDriverTop5Block = (
  serialized: string,
  answersArrayData: readonly (readonly FiveSegDriverSourceRow[])[],
  t: TFunction,
  hiddenColumnKeys: readonly FiveSegmentColumnKey[] = [],
): string => {
  // 5segs の差分列ラベル (ロイヤル vs 一般 等) と違い、ここでは移動の向きが分かる
  // 「認知未購入→一般」形式にする。プロンプトの出力フォーマット行と同じ向き。
  const pairs = [
    {
      from: fiveSegmentLabelI18nKeyMap.fiveSegment4,
      to: fiveSegmentLabelI18nKeyMap.fiveSegment2,
      diffKey: 'generalVsRecognized',
    },
    {
      from: fiveSegmentLabelI18nKeyMap.fiveSegment3,
      to: fiveSegmentLabelI18nKeyMap.fiveSegment2,
      diffKey: 'generalVsEstranged',
    },
    {
      from: fiveSegmentLabelI18nKeyMap.fiveSegment2,
      to: fiveSegmentLabelI18nKeyMap.fiveSegment1,
      diffKey: 'loyalVsGeneral',
    },
  ] as const

  const questions = answersArrayData
    .filter((rows) => 0 < rows.length)
    .map((rows) => ({
      questionText: rows[0].question.question_text,
      lines: pairs.map(({ diffKey, from, to }) => ({
        label: `${t(from)}→${t(to)}`,
        // 差分列が非表示なら値を持たせず DRIVER_NONE_MARK 行にする (9segs 版と同じ)。
        entries: hiddenColumnKeys.includes(diffKey)
          ? []
          : rows.map((row) => ({
              text: row.answerOption.text,
              diff: row.ratios[diffKey],
            })),
      })),
    }))

  const block = renderBlock(FIVE_SEG_DRIVER_BLOCK_HEADING, questions)

  return block === null ? serialized : `${serialized}\n\n${block}`
}
